<template>
  <div
    class="message-row mb-1"
    :class="isOwn ? 'message-row--own' : 'message-row--other'"
    :data-message-id="message.id"
  >
    <!-- Sender avatar (others only, group only) -->
    <v-avatar
      v-if="!isOwn && isGroup"
      size="28"
      class="message-avatar mr-2 flex-shrink-0"
    >
      <v-img v-if="senderAvatar" :src="senderAvatar" />
      <div
        v-else
        class="d-flex align-center justify-center white--text"
        :style="`background: ${senderColor}; width: 100%; height: 100%; border-radius: 50%; font-size: 12px; font-weight: 600`"
      >
        {{ senderInitial }}
      </div>
    </v-avatar>

    <!-- Bubble wrapper -->
    <div class="bubble-wrapper" :class="isOwn ? 'bubble-wrapper--own' : 'bubble-wrapper--other'">
      <!-- Bubble -->
      <div
        class="bubble"
        :class="[
          isOwn ? 'bubble--own' : 'bubble--other',
          message.error ? 'bubble--error' : ''
        ]"
      >
        <!-- Sender name (group, others only) -->
        <div v-if="!isOwn && isGroup" class="sender-name mb-1">
          {{ message.sender?.first_name || 'Unknown' }}
        </div>

        <!-- Image attachment -->
        <v-img
          v-if="fileUrl && isImage()"
          :src="fileUrl"
          max-width="260"
          class="bubble-image rounded mb-1"
          style="cursor: pointer"
          @click="$emit('imageClick', fileUrl)"
        />

        <!-- File attachment (non-image) -->
        <div v-else-if="fileUrl" class="file-attachment mb-1">
          <v-icon size="20" class="mr-1">mdi-file-document-outline</v-icon>
          <a :href="fileUrl" target="_blank" class="file-link">{{ fileName }}</a>
        </div>

        <!-- Message text -->
        <div v-if="message.message" class="message-text">{{ message.message }}</div>

        <!-- Sending indicator -->
        <div v-if="message.sending" class="meta-row mt-1">
          <v-progress-circular indeterminate size="12" width="2" color="white" class="mr-1" />
          <span class="meta-text">Sending…</span>
        </div>

        <!-- Error indicator -->
        <div v-else-if="message.error" class="meta-row mt-1">
          <v-icon size="14" color="white" class="mr-1">mdi-alert-circle-outline</v-icon>
          <span class="meta-text">Failed —</span>
          <span class="meta-text retry-link ml-1" @click="$emit('retry', message)">Retry</span>
        </div>

        <!-- Timestamp + seen status -->
        <div v-else class="meta-row mt-1" :class="isOwn ? 'justify-end' : ''">
          <span class="meta-text">{{ formattedTime }}</span>
          <v-icon
            v-if="isOwn"
            size="14"
            :color="message.seen ? '#4fc3f7' : 'rgba(255,255,255,0.7)'"
            class="ml-1"
          >
            {{ message.seen ? 'mdi-check-all' : 'mdi-check' }}
          </v-icon>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
const AVATAR_COLORS = [
  '#e53935', '#d81b60', '#8e24aa', '#5e35b1',
  '#1e88e5', '#00897b', '#43a047', '#fb8c00',
  '#f4511e', '#6d4c41', '#546e7a', '#039be5',
];

export default {
  name: 'MessageBubble',

  props: {
    message: { type: Object, required: true },
    isOwn: { type: Boolean, default: false },
    isGroup: { type: Boolean, default: false }
  },

  computed: {
    fileUrl() {
      if (!this.message.file_path) return null;
      if (this.message.file_path.startsWith('blob:')) return this.message.file_path;
      return this.mediaUrl + this.message.file_path;
    },

    fileName() {
      if (!this.message.file_path) return '';
      return this.message.file_path.split('/').pop();
    },

    senderAvatar() {
      const img = this.message.sender?.image_path;
      return img ? this.mediaUrl + img : null;
    },

    senderInitial() {
      return (this.message.sender?.first_name || 'U').charAt(0).toUpperCase();
    },

    senderColor() {
      const seed = (this.message.sender?.first_name || '').charCodeAt(0) || 0;
      return AVATAR_COLORS[seed % AVATAR_COLORS.length];
    },

    formattedTime() {
      if (!this.message.created_at) return '';
      return new Date(this.message.created_at).toLocaleTimeString('en-US', {
        hour: 'numeric',
        minute: '2-digit'
      });
    }
  },

  methods: {
    isImage() {
      const ref = this.message.filename || this.message.file_path || this.fileUrl || '';
      const ext = ref.split('?')[0].split('.').pop().toLowerCase();
      return ['jpg', 'jpeg', 'png', 'gif', 'webp', 'bmp', 'svg'].includes(ext);
    }
  }
};
</script>

<style scoped>
/* Row layout */
.message-row {
  display: flex;
  align-items: flex-end;
  padding: 1px 12px;
}

.message-row--own {
  flex-direction: row-reverse;
}

.message-row--other {
  flex-direction: row;
}

/* Bubble wrapper constrains width */
.bubble-wrapper {
  max-width: 68%;
  display: flex;
  flex-direction: column;
}

.bubble-wrapper--own {
  align-items: flex-end;
}

.bubble-wrapper--other {
  align-items: flex-start;
}

/* Bubble base */
.bubble {
  position: relative;
  padding: 8px 12px 6px;
  border-radius: 8px;
  word-wrap: break-word;
  word-break: break-word;
  box-shadow: 0 1px 1px rgba(0, 0, 0, 0.13);
}

/* Own message — green */
.bubble--own {
  background: #dcf8c6;
  color: #111;
  border-top-right-radius: 2px;
}

/* Own message tail */
.bubble--own::after {
  content: '';
  position: absolute;
  top: 0;
  right: -8px;
  width: 0;
  height: 0;
  border-style: solid;
  border-width: 0 0 10px 10px;
  border-color: transparent transparent transparent #dcf8c6;
}

/* Other message — white */
.bubble--other {
  background: #ffffff;
  color: #111;
  border-top-left-radius: 2px;
}

/* Other message tail */
.bubble--other::after {
  content: '';
  position: absolute;
  top: 0;
  left: -8px;
  width: 0;
  height: 0;
  border-style: solid;
  border-width: 0 10px 10px 0;
  border-color: transparent #ffffff transparent transparent;
}

/* Error bubble */
.bubble--error {
  background: #ffcdd2 !important;
}

/* Sender name in group */
.sender-name {
  font-size: 12px;
  font-weight: 700;
  color: #1e88e5;
  line-height: 1.2;
}

/* Message text */
.message-text {
  font-size: 14px;
  line-height: 1.45;
  white-space: pre-wrap;
}

/* Meta row (time + ticks) */
.meta-row {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  flex-wrap: nowrap;
  gap: 2px;
}

.meta-text {
  font-size: 11px;
  color: rgba(0, 0, 0, 0.45);
  line-height: 1;
  white-space: nowrap;
}

.retry-link {
  cursor: pointer;
  color: #e53935;
  text-decoration: underline;
}

/* Image inside bubble */
.bubble-image {
  border-radius: 6px;
  max-width: 100%;
}

/* File attachment row */
.file-attachment {
  display: flex;
  align-items: center;
  background: rgba(0, 0, 0, 0.06);
  border-radius: 6px;
  padding: 6px 10px;
}

.file-link {
  color: #1e88e5;
  font-size: 13px;
  text-decoration: none;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  max-width: 200px;
}

.file-link:hover {
  text-decoration: underline;
}

/* Avatar for group sender */
.message-avatar {
  align-self: flex-start;
}
</style>
